import {
    createSelector,
    createEntityAdapter
} from "@reduxjs/toolkit";
import { apiSlice } from "../../app/api/apiSlice"

const complaintsAdapter = createEntityAdapter({});

const initialState = complaintsAdapter.getInitialState();

export const complaintsApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getComplaints: builder.query({
            query: () => ({
                url: '/complaints',
                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                },
            }),
            transformResponse: responseData => {
                const loadedComplaints = responseData.map(complaints => {
                    // console.log(assetmaster)
                    complaints.id = complaints._id
                    // console.log(assetmaster)
                    return complaints
                });
                return complaintsAdapter.setAll(initialState, loadedComplaints)
            },
            providesTags: (result, error, arg) => {
                if (result?.ids) {
                    return [
                        { type: 'Complaints', id: 'LIST' },
                        ...result.ids.map(id => ({ type: 'Complaints', id }))
                    ]
                } else return [{ type: 'Complaints', id: 'LIST' }]
            }
        }),
        getFilteredComplaints: builder.query({
            query: ({ page, limit, filters = {} }) => {
                return {
                    url: "/complaints/filtered",
                    params: { filters, page, limit },
                    validateStatus: (response) => response.status === 200,
                };
            },
            transformResponse: (responseData) => {
                const transformedData = {
                    complaints: responseData.complaints?.map((complaint) => ({
                        ...complaint,
                        id: complaint._id, // Ensure `id` is mapped correctly
                    })) || [],
                    totalFilteredComplaints: responseData.totalFilteredComplaints || 0,
                    totalPages: responseData.totalPages || 1,
                };
                return transformedData;
            },
            providesTags: (result) =>
                result?.complaints
                ? [
                    { type: "Complaints", id: "LIST" },
                    ...result.complaints.map((complaint) => ({
                        type: "Complaints",
                        id: complaint.id,
                    })),
                    ]
                : [{ type: "Complaints", id: "LIST" }],
        }),
        getFilteredComplaintsLazy: builder.query({
            query: ({ page, limit, filters = {} }) => ({
                url: "/complaints/filtered",
                params: { filters, page, limit },
                validateStatus: (response) => response.status === 200,
            }),
            transformResponse: (responseData) => ({
                complaints: responseData.complaints?.map((complaint) => ({
                    ...complaint,
                    id: complaint._id,
                })) || [],
                totalFilteredComplaints: responseData.totalFilteredComplaints || 0,
                totalPages: responseData.totalPages || 1,
            }),
            providesTags: (result) =>
                result?.complaints
                ? [
                    { type: "Complaints", id: "LIST" },
                    ...result.complaints.map((complaint) => ({
                        type: "Complaints",
                        id: complaint.id,
                    })),
                    ]
                : [{ type: "Complaints", id: "LIST" }],
        }),
        addNewComplaints: builder.mutation({
            query: initialUserData => ({
                url: '/complaints',
                method: 'POST',
                body: {
                    ...initialUserData,
                }
            }),
            invalidatesTags: [
                { type: 'Complaints', id: "LIST" }
            ]
        }),
        updateComplaints: builder.mutation({
            query: initialUserData => ({
                url: '/complaints',
                method: 'PUT',
                body: {
                    ...initialUserData,
                }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Complaints', id: arg.id }
            ]
        }),
       deleteComplaints: builder.mutation({
            query: ({ id }) => ({
                url: `/complaints`,
                method: 'DELETE',
                body: { id }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Complaints', id: arg.id }
            ]
        }),
        markAsRead: builder.mutation({
            query: ({ requestId, requestType }) => ({
                url: '/complaints/mark-as-read',
                method: 'PUT',
                body: {
                    requestId,
                    requestType
                }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Complaints', id: arg.requestId }
            ]
        }),
    }),
})

export const {
    useGetComplaintsQuery,
    useGetFilteredComplaintsQuery,
    useLazyGetFilteredComplaintsLazyQuery,
    useAddNewComplaintsMutation,
    useUpdateComplaintsMutation,
    useDeleteComplaintsMutation,
    useMarkAsReadMutation   
} = complaintsApiSlice

// returns the query result object
export const selectComplaintsResult = complaintsApiSlice.endpoints.getComplaints.select()

// creates memoized selector
const selectComplaintsData = createSelector(
    selectComplaintsResult,
    complaintsResult => complaintsResult.data // normalized state object with ids & entities
)

//getSelectors creates these selectors and we rename them with aliases using destructuring
export const {
    selectAll: selectAllComplaints,
    selectById: selectComplaintsById,
    selectIds: selectComplaintsIds
    // Pass in a selector that returns the users slice of state
} = complaintsAdapter.getSelectors(state => selectComplaintsData(state) ?? initialState)